import { createFileRoute, Link } from "@tanstack/react-router";
import { trackConversion } from "@/lib/gtag";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { IdleCallNudge } from "@/components/IdleCallNudge";
import { Phone, ShieldCheck, Clock, Car, Briefcase, Plane, Star, MapPin } from "lucide-react";

const URL = "https://sepremiumcabs.com/chauffeur-services";
const TITLE = "Chauffeur Melbourne — SE Premium Cabs";
const DESC =
  "Professional chauffeur service in Melbourne. Private drivers and luxury vehicles for airport transfers and events. Fixed pricing, 24/7. Call 0493 100 265.";

export const Route = createFileRoute("/chauffeur-services")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Chauffeur service",
          provider: {
            "@type": "LocalBusiness",
            name: "SE Premium Cabs",
            telephone: "+61493100265",
            email: "sepremiumcabs@gmail.com",
            url: "https://sepremiumcabs.com/",
            areaServed: "Melbourne, Victoria, Australia",
          },
          areaServed: "Melbourne, Victoria, Australia",
          name: "Melbourne Chauffeur & Private Driver Service",
          description: DESC,
        }),
      },
    ],
  }),
  component: ChauffeurPage,
});

function ChauffeurPage() {
  const points = [
    { icon: Car, title: "Luxury Fleet", body: "Late-model Mercedes-class sedans, spacious SUVs and 11-seat Maxi vans — spotless, non-smoking, sanitised after every ride." },
    { icon: ShieldCheck, title: "Professional Chauffeurs", body: "CPV-accredited, background-checked, presentable drivers who know Melbourne's roads and shortcuts." },
    { icon: Clock, title: "24/7 On-Demand & Pre-Book", body: "Available around the clock. Pre-book weeks in advance or call for on-demand pickups — we answer every call." },
    { icon: Plane, title: "Airport Meet & Greet", body: "Flight tracking, name-board meet & greet at Melbourne Airport (Tullamarine), luggage assistance, complimentary wait time." },
    { icon: Briefcase, title: "Corporate Accounts", body: "Monthly invoicing, priority dispatch, dedicated drivers for executives, and confidential travel for board meetings." },
    { icon: Star, title: "Fixed Upfront Fares", body: "Quoted before you travel — no surge, no meter surprises, no cancellation traps for pre-bookings." },
  ];
  const useCases = [
    "Melbourne Airport transfers (MEL / Tullamarine)",
    "Executive & corporate travel",
    "Weddings and formal events",
    "Winery and Yarra Valley day tours",
    "Interstate airport connections (Avalon)",
    "Hotel-to-venue transfers for guests",
  ];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="chauffeur" />
      <div className="mx-auto max-w-4xl px-6 py-12 pb-28 lg:py-20 lg:pb-20">
        <nav className="mb-8 text-xs font-semibold tracking-widest text-muted-foreground">
          <Link to="/" className="hover:text-primary">HOME</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">CHAUFFEUR SERVICES</span>
        </nav>
        <h1 className="font-serif text-4xl font-bold tracking-tight lg:text-5xl">
          Melbourne Chauffeur &<br />
          <span className="text-primary">Private Driver Service</span>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
          SE Premium Cabs delivers a full chauffeur experience across Melbourne — the comfort
          and discretion of a private driver, with the reliability and upfront pricing of a
          professional cab. Perfect for airport transfers, corporate travel, weddings and
          events across South East Melbourne, the Eastern suburbs and Melbourne Airport.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground transition-transform hover:scale-[1.02]"
          >
            <Phone className="h-4 w-4" />
            CALL 0493 100 265
          </a>
          <Link
            to="/"
            hash="book"
            className="flex items-center gap-2 rounded-md border border-primary px-6 py-3 text-sm font-bold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            BOOK ONLINE
          </Link>
        </div>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Chauffeur vs Standard Taxi — What's the Difference?
          </h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            A standard taxi gets you from A to B. A chauffeur service delivers a curated
            travel experience — a premium vehicle, a professional driver in business attire,
            a fixed upfront fare, and service standards built around your comfort. No shared
            rides, no surge pricing, no unpredictable waits. Just a driver who arrives early,
            handles your luggage, and gets you where you need to go — on time, every time.
          </p>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Why Choose SE Premium Cabs
          </h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {points.map((p) => (
              <div key={p.title} className="rounded-lg border border-border/60 bg-card p-5">
                <div className="flex items-center gap-3">
                  <p.icon className="h-5 w-5 text-primary" />
                  <h3 className="text-sm font-bold tracking-wider">{p.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            When to Book a Chauffeur
          </h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <ul className="mt-6 grid gap-3 md:grid-cols-2">
            {useCases.map((u) => (
              <li key={u} className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>{u}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 rounded-xl border border-primary/40 bg-card p-8 text-center">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Ready to book your Melbourne chauffeur?
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Available 24/7 across Melbourne. Fixed upfront quotes.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
              className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground"
            >
              <Phone className="h-4 w-4" /> CALL 0493 100 265
            </a>
            <Link
              to="/"
              hash="book"
              className="flex items-center gap-2 rounded-md border border-primary px-6 py-3 text-sm font-bold tracking-wider text-primary"
            >
              BOOK ONLINE
            </Link>
          </div>
        </section>
      </div>
      <SiteFooter />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}